import clone from 'lodash/clone';
import merge from 'lodash/merge';
import { SiteType } from 'types/site.types';
const BatchTracingReducer = (state, action) => {
    var _a;
    switch (action.type) {
        case 'OPEN_DIALOG':
            if (!((_a = state.latestBatch.rawMaterials) === null || _a === void 0 ? void 0 : _a.length)) {
                console.error('No traceable raw materials found');
                return state;
            }
            return Object.assign(Object.assign({}, state), { dialogOpen: true });
        case 'CLOSE_DIALOG':
            return Object.assign(Object.assign({}, state), { dialogOpen: false });
        case 'SET_BATCH_TRACING_INACTIVE':
            return Object.assign(Object.assign({}, state), { mappedFilteredSteps: state.steps, filteredRawMaterials: state.allRawMaterials, traceableChainStepActivitiesIds: undefined, isBatchTracingActive: false });
        /**
         * Map over the steps and chainStepActivities to
         * - filter activities that are not traceable (not included in the traceableRawMaterialIds)
         * - add the transportation mode to the incoming links
         * - populate with batch data
         */
        case 'SET_BATCH_TRACING_ACTIVE': {
            if (!action.batchData) {
                // eslint-disable-next-line no-console
                console.warn('No batch data found');
                return state;
            }
            const traceableChainStepActivitiesIds = action.batchData.traceableChainStepActivitiesIds;
            const mappedFilteredSteps = state.steps.map((step) => {
                var _a;
                const filteredChainActivities = step.chainStepActivities;
                // Helper array with all the chain activities containing the batch data
                const batchTracingChainStepActivities = (_a = action.batchData) === null || _a === void 0 ? void 0 : _a.traceableRawMaterials.flatMap(({ chainStepActivities }) => chainStepActivities);
                // Sometimes the multiple raw materials have the same chain activity so we have to filter out batchTracingChainStepActivities with the same id and combine them into one, while only changing the siteBatchDetails
                // Create a map to group activities by id
                const activityMap = new Map();
                // Iterate over batchTracingChainStepActivities and populate the map
                batchTracingChainStepActivities.forEach((activity) => {
                    if (activityMap.has(activity.id)) {
                        // Combine activities with the same id by merging their siteBatchDetails
                        const existingActivity = activityMap.get(activity.id);
                        existingActivity.siteBatchDetails = [
                            ...existingActivity.siteBatchDetails,
                            ...activity.siteBatchDetails,
                        ];
                    }
                    else {
                        activityMap.set(activity.id, Object.assign({}, activity));
                    }
                });
                // Convert the map back to an array
                const combinedBatchTracingChainStepActivities = Array.from(activityMap.values());
                const mappedFilteredChainActivities = filteredChainActivities.map((chainStepActivity) => {
                    var _a, _b, _c, _d, _e, _f, _g, _h;
                    const batchChainStepActivity = combinedBatchTracingChainStepActivities.find(({ id }) => id === chainStepActivity.id);
                    if (!batchChainStepActivity) {
                        // eslint-disable-next-line no-console
                        console.warn('No batch data found for chainStepActivity');
                        return chainStepActivity;
                    }
                    /**
                     * Fixing the merge issue that happens because edges nodes do not contain an id directly and will be merged by index
                     * Since the indices are not the same, the merge will not work as expected so we sort them before by id and then merge
                     */
                    if (((_a = chainStepActivity.activity.site) === null || _a === void 0 ? void 0 : _a.siteType) === SiteType.CLUSTER &&
                        ((_b = batchChainStepActivity.activity.site) === null || _b === void 0 ? void 0 : _b.siteType) ===
                            SiteType.CLUSTER) {
                        (_e = (_d = (_c = batchChainStepActivity.activity.site) === null || _c === void 0 ? void 0 : _c.sites) === null || _d === void 0 ? void 0 : _d.edges) === null || _e === void 0 ? void 0 : _e.sort((a, b) => (a.node.id > b.node.id ? 1 : -1));
                        (_h = (_g = (_f = chainStepActivity.activity.site) === null || _f === void 0 ? void 0 : _f.sites) === null || _g === void 0 ? void 0 : _g.edges) === null || _h === void 0 ? void 0 : _h.sort((a, b) => a.node.id > b.node.id ? 1 : -1);
                    }
                    // This should merge the two objects and add batch data to the chainStepActivity
                    return merge(clone(chainStepActivity), batchChainStepActivity);
                });
                // Sort activities so the activities with batch data is always first in the list
                const mappedFilteredSortedChainActivities = mappedFilteredChainActivities.sort((a, b) => {
                    if (traceableChainStepActivitiesIds.includes(a.id) &&
                        !traceableChainStepActivitiesIds.includes(b.id)) {
                        return -1;
                    }
                    if (!traceableChainStepActivitiesIds.includes(a.id) &&
                        traceableChainStepActivitiesIds.includes(b.id)) {
                        return 1;
                    }
                    return 0;
                });
                return Object.assign(Object.assign({}, step), { chainStepActivities: mappedFilteredSortedChainActivities });
            });
            const traceableRawMaterialIds = action.batchData.traceableRawMaterials.map(({ id }) => id);
            const filteredRawMaterials = state.allRawMaterials
                .filter(({ id }) => traceableRawMaterialIds.includes(id))
                .map((rawMaterial) => {
                const batchRawMaterial = action.batchData.traceableRawMaterials.find(({ id }) => id === rawMaterial.id);
                if (!batchRawMaterial) {
                    // eslint-disable-next-line no-console
                    console.warn('No batch data found for rawMaterial');
                    return rawMaterial;
                }
                return Object.assign(Object.assign({}, rawMaterial), { batchDetails: batchRawMaterial.batchDetails });
            });
            return Object.assign(Object.assign({}, state), { isBatchTracingActive: true, dialogOpen: false, mappedFilteredSteps,
                filteredRawMaterials,
                traceableChainStepActivitiesIds });
        }
        default:
            return state;
    }
};
export default BatchTracingReducer;
